import { CompleteKundali, Language, PlanetPosition, HouseInfo, BirthDetails, DashaPeriod } from '../types/kundali';
import { calculateShadbala, getBirthUtcDate, ZODIAC_SIGNS, getSavByBhava } from './vedicEngine';
import { extractPersonalitySignature, PersonalitySignature } from './integratedProfileGenerator';
import { formatOrdinal } from './dateUtils';
import {
  PrimaryDomain,
  EventType,
  QuestionType,
  QuestionClassification,
  classifyAstrologicalQuestion,
  evaluateD10Dashamsha,
  D10AnalysisResult,
  EventTimingAnalysis
} from './astrologerConsultationEngine';
import { AstrologicalEvidenceItem } from '../types/predictionDifferentiation';

// ============================================================================
// 1. FORENSIC HASHES & DIAGNOSTIC INTERFACES
// ============================================================================

export interface ForensicDiagnosticHashes {
  nativeId: string;
  chartHash: string;
  analysisHash: string;
  questionHash: string;
  personalityHash: string;
  lifeAreaHash: string;
  timingHash: string;
  evidenceHash: string;
  generatedTimestamp: string;
  engineVersion: string;
}

export type TwoNativeTwoQuestionVerificationResult = VerificationTestResult;

export interface DynamicBhavaAnalysis {
  houseNumber: number;
  sign: string;
  lord: string;
  lordPlacementHouse: number;
  lordSign: string;
  lordDignity: string;
  occupants: string[];
  aspectingPlanets: string[];
  savPoints: number;
  strengthScore: number;
  functionalNature: 'Favorable' | 'Challenging' | 'Mixed';
}

export interface DynamicDomainMatrix {
  domain: PrimaryDomain;
  primaryHouses: number[];
  primaryKarakas: string[];
  promiseStrength: number; // 0 to 100
  promiseVerdictEn: string;
  promiseVerdictHi: string;
  supportingFactors: string[];
  challengingFactors: string[];
  divisionalRefinement: string;
}

export interface DynamicTimingCandidate {
  label: string;
  dateRange: string;
  mahadashaLord: string;
  antardashaLord: string;
  favorableScore: number;
  astrologicalMechanismEn: string;
  astrologicalMechanismHi: string;
}

export interface DynamicAstrologicalAnalysis {
  nativeId: string;
  nativeAge: number | null;
  chartSummary: {
    lagnaSign: string;
    lagnaLord: string;
    lagnaLordPlacement: string;
    moonSign: string;
    moonNakshatra: string;
    moonPada: number;
    sunSign: string;
  };
  personalitySignature: PersonalitySignature;
  bhavas: DynamicBhavaAnalysis[];
  domains: Record<PrimaryDomain, DynamicDomainMatrix>;
  activeDasha: {
    mahadasha: string;
    antardasha: string;
    startDate: string;
    endDate: string;
    upcomingTimeline: Array<{ planet: string; startDate: string; endDate: string }>;
  };
  gocharTransits: {
    saturnSign: string;
    saturnHouse: number;
    jupiterSign: string;
    jupiterHouse: number;
    rahuSign: string;
    rahuHouse: number;
    ketuSign: string;
    ketuHouse: number;
    doubleTransitActive: boolean;
    doubleTransitHouses: number[];
  };
  questionClassification: QuestionClassification;
  questionEvidence: {
    primaryHousesLords: Array<{ house: number; sign: string; lord: string; dignity: string; placement: number }>;
    occupants: Array<{ house: number; planetName: string; dignity: string }>;
    karakas: Array<{ name: string; dignity: string; house: number; significance: string }>;
    d10Analysis: D10AnalysisResult;
    savProfile: Array<{ house: number; points: number }>;
    evidenceItems: AstrologicalEvidenceItem[];
  };
  timingModel: {
    isTimingQuestion: boolean;
    strongestWindow: DynamicTimingCandidate | null;
    secondaryWindow: DynamicTimingCandidate | null;
    qualifications: string[];
    practicalGuidance: string[];
  };
  synthesisVerdictEn: string;
  synthesisVerdictHi: string;
  forensicHashes: ForensicDiagnosticHashes;
}

// ============================================================================
// 2. CRYPTOGRAPHIC / DETERMINISTIC STRING HASHING UTILITY
// ============================================================================

/**
 * Fast, deterministic, 64-bit FNV-1a / Murmur-inspired string hashing function
 * yielding distinct 16-character hexadecimal hash representations.
 */
export function generateDeterministicHash(input: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x1000193;

  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    h1 = (h1 ^ char) * 0x01000193;
    h2 = (h2 ^ ((char << 5) + char)) * 0x811c9dc5;
    h1 = Math.imul(h1, 0x5bd1e995) ^ (h1 >>> 15);
    h2 = Math.imul(h2, 0x1b873593) ^ (h2 >>> 13);
  }

  const part1 = (h1 >>> 0).toString(16).padStart(8, '0');
  const part2 = (h2 >>> 0).toString(16).padStart(8, '0');
  return `${part1}${part2}`;
}

// ============================================================================
// 3. CORE DYNAMIC ASTROLOGICAL ANALYSIS ENGINE
// ============================================================================

const SIGN_LORDS: Record<string, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

function getSignLord(sign: string): string {
  return SIGN_LORDS[sign] || 'Mars';
}

/**
 * Computes native's age in full years from birth date.
 */
export function computeNativeAge(birthDetails?: BirthDetails, referenceDate?: Date | string): number | null {
  if (!birthDetails?.date) return null;
  const bDate = new Date(birthDetails.date);
  if (isNaN(bDate.getTime())) return null;
  const ref = referenceDate ? new Date(referenceDate) : new Date();
  if (isNaN(ref.getTime())) return null;
  let age = ref.getFullYear() - bDate.getFullYear();
  const m = ref.getMonth() - bDate.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < bDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
}

/**
 * Generates Native ID deterministically.
 */
export function generateNativeId(kundali: CompleteKundali): string {
  const b: Partial<BirthDetails> = kundali.birthDetails || {};
  const name = (b.name || 'Native').trim().replace(/\s+/g, '_');
  const date = b.date || 'UNKNOWN_DATE';
  const time = b.time || 'UNKNOWN_TIME';
  const lat = b.latitude !== undefined ? Number(b.latitude).toFixed(2) : '0.00';
  const lon = b.longitude !== undefined ? Number(b.longitude).toFixed(2) : '0.00';
  return `${name}_${date}_${time}_${lat}_${lon}`;
}

/**
 * Calculates complete real Bhava analyses from planetary positions.
 */
export function calculateDynamicBhavas(kundali: CompleteKundali): DynamicBhavaAnalysis[] {
  const houses: HouseInfo[] = Array.isArray(kundali.houses) ? kundali.houses : [];
  const planets: PlanetPosition[] = Array.isArray(kundali.planets) ? kundali.planets : [];
  const sav = kundali.ashtakvarga?.sarvashtakvarga || [28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];

  const results: DynamicBhavaAnalysis[] = [];

  for (let i = 1; i <= 12; i++) {
    const h = houses.find(house => house.houseNumber === i) || houses[i - 1] || { houseNumber: i, sign: 'Aries', signLord: 'Mars' };
    const sign = h.sign || 'Aries';
    const lord = h.signLord || getSignLord(sign);
    const lordPlanet = planets.find(p => p.name === lord);

    const lordPlacementHouse = lordPlanet?.house || 1;
    const lordSign = lordPlanet?.sign || sign;
    const lordDignity = lordPlanet?.dignity || 'Own House';

    const occupants = planets.filter(p => p.house === i && p.name !== 'Ascendant').map(p => p.name);

    // Simple Vedic aspects calculation
    const aspectingPlanets: string[] = [];
    planets.forEach(p => {
      if (p.name === 'Ascendant' || p.house === i) return;
      const dist = (i - p.house + 12) % 12 + 1;
      // 7th house aspect for all planets
      if (dist === 7) aspectingPlanets.push(p.name);
      // Special aspects: Mars (4, 8), Jupiter (5, 9), Saturn (3, 10), Rahu/Ketu (5, 9)
      if (p.name === 'Mars' && (dist === 4 || dist === 8)) aspectingPlanets.push('Mars');
      if (p.name === 'Jupiter' && (dist === 5 || dist === 9)) aspectingPlanets.push('Jupiter');
      if (p.name === 'Saturn' && (dist === 3 || dist === 10)) aspectingPlanets.push('Saturn');
      if ((p.name === 'Rahu' || p.name === 'Ketu') && (dist === 5 || dist === 9)) aspectingPlanets.push(p.name);
    });

    const savPoints = getSavByBhava(kundali.ashtakvarga, i, kundali.houses);

    // Mathematical strength score based on lord dignity, SAV, and occupants
    let strengthScore = 50;
    if (['Exalted', 'Moolatrikona'].includes(lordDignity)) strengthScore += 25;
    else if (lordDignity === 'Own House') strengthScore += 18;
    else if (lordDignity === 'Debilitated') strengthScore -= 20;

    if (savPoints >= 30) strengthScore += 15;
    else if (savPoints < 25) strengthScore -= 12;

    const hasBenefic = occupants.some(p => ['Jupiter', 'Venus', 'Mercury', 'Moon'].includes(p));
    const hasMalefic = occupants.some(p => ['Saturn', 'Mars', 'Rahu', 'Ketu', 'Sun'].includes(p));
    if (hasBenefic && !hasMalefic) strengthScore += 10;
    if (hasMalefic && !hasBenefic) strengthScore -= 10;

    strengthScore = Math.max(10, Math.min(100, strengthScore));

    let functionalNature: 'Favorable' | 'Challenging' | 'Mixed' = 'Mixed';
    if (strengthScore >= 65) functionalNature = 'Favorable';
    else if (strengthScore <= 40) functionalNature = 'Challenging';

    results.push({
      houseNumber: i,
      sign,
      lord,
      lordPlacementHouse,
      lordSign,
      lordDignity,
      occupants,
      aspectingPlanets: Array.from(new Set(aspectingPlanets)),
      savPoints,
      strengthScore,
      functionalNature
    });
  }

  return results;
}

/**
 * Calculates dynamic domain matrices across primary life themes.
 */
export function calculateDynamicDomains(
  bhavas: DynamicBhavaAnalysis[],
  planets: PlanetPosition[],
  shadbalaFactors: Array<{ planet: string; rupas: number }>
): Record<PrimaryDomain, DynamicDomainMatrix> {
  const getBhava = (num: number) => bhavas.find(b => b.houseNumber === num) || bhavas[0];
  const getPlanet = (name: string) => planets.find(p => p.name === name);

  const domains: Record<PrimaryDomain, DynamicDomainMatrix> = {
    career: {
      domain: 'career',
      primaryHouses: [10, 6, 11, 1],
      primaryKarakas: ['Sun', 'Saturn', 'Mercury'],
      promiseStrength: Math.round((getBhava(10).strengthScore * 0.45 + getBhava(6).strengthScore * 0.25 + getBhava(11).strengthScore * 0.3)),
      promiseVerdictEn: `10th house (${getBhava(10).sign}) lord ${getBhava(10).lord} placed in House ${getBhava(10).lordPlacementHouse} with ${getBhava(10).lordDignity} status, conferring stable professional execution.`,
      promiseVerdictHi: `दशम भाव (${getBhava(10).sign}) के स्वामी ${getBhava(10).lord} भाव ${getBhava(10).lordPlacementHouse} में ${getBhava(10).lordDignity} स्थिति में हैं, जो कार्यक्षेत्र में स्थायित्व प्रदान करते हैं।`,
      supportingFactors: [`10H Lord ${getBhava(10).lord} in H${getBhava(10).lordPlacementHouse}`, `11H SAV: ${getBhava(11).savPoints} pts`],
      challengingFactors: getBhava(6).occupants.length > 0 ? [`6H Occupants: ${getBhava(6).occupants.join(', ')}`] : [],
      divisionalRefinement: 'D10 Dashamsha validates executive status and competitive resilience.'
    },
    business: {
      domain: 'business',
      primaryHouses: [7, 10, 11, 2],
      primaryKarakas: ['Mercury', 'Jupiter'],
      promiseStrength: Math.round((getBhava(7).strengthScore * 0.4 + getBhava(11).strengthScore * 0.35 + getBhava(2).strengthScore * 0.25)),
      promiseVerdictEn: `Commercial 7H/11H axis governed by ${getBhava(7).lord} and ${getBhava(11).lord} indicates structured enterprise capacity.`,
      promiseVerdictHi: `सप्तम एवं एकादश भाव व्यापारिक साझेदारी एवं व्यावसायिक लाभ को सुदृढ़ करते हैं।`,
      supportingFactors: [`7H Lord ${getBhava(7).lord} in ${getBhava(7).lordDignity}`, `2H SAV: ${getBhava(2).savPoints} pts`],
      challengingFactors: [],
      divisionalRefinement: 'D10 and D11 support commercial negotiation cycles.'
    },
    wealth: {
      domain: 'wealth',
      primaryHouses: [2, 11, 5, 9],
      primaryKarakas: ['Jupiter', 'Venus'],
      promiseStrength: Math.round((getBhava(2).strengthScore * 0.35 + getBhava(11).strengthScore * 0.35 + getBhava(9).strengthScore * 0.3)),
      promiseVerdictEn: `Dhana Bhavas (2H/11H) hold ${getBhava(2).savPoints} & ${getBhava(11).savPoints} SAV points, securing disciplined wealth accumulation.`,
      promiseVerdictHi: `धन एवं लाभ भाव (${getBhava(2).savPoints} व ${getBhava(11).savPoints} अष्टकवर्ग अंक) वित्तीय संचय में सहायता करते हैं।`,
      supportingFactors: [`2H Lord ${getBhava(2).lord} in H${getBhava(2).lordPlacementHouse}`, `11H Lord ${getBhava(11).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D2 Hora indicates solid material asset preservation.'
    },
    marriage: {
      domain: 'marriage',
      primaryHouses: [7, 2, 11],
      primaryKarakas: ['Venus', 'Jupiter'],
      promiseStrength: Math.round((getBhava(7).strengthScore * 0.5 + getBhava(2).strengthScore * 0.3 + getBhava(11).strengthScore * 0.2)),
      promiseVerdictEn: `7th house (${getBhava(7).sign}) ruled by ${getBhava(7).lord} governs marital harmony and partnership alignment.`,
      promiseVerdictHi: `सप्तम भाव (${getBhava(7).sign}) के स्वामी ${getBhava(7).lord} वैवाहिक समरसता और साझेदारी को दिशा देते हैं।`,
      supportingFactors: [`7H Lord ${getBhava(7).lord} in H${getBhava(7).lordPlacementHouse}`],
      challengingFactors: getBhava(7).occupants.length > 0 ? [`Occupants in 7H: ${getBhava(7).occupants.join(', ')}`] : [],
      divisionalRefinement: 'D9 Navamsha establishes relationship maturity and spouse temperament.'
    },
    relationship: {
      domain: 'relationship',
      primaryHouses: [5, 7, 11],
      primaryKarakas: ['Venus', 'Moon'],
      promiseStrength: Math.round((getBhava(5).strengthScore * 0.5 + getBhava(7).strengthScore * 0.5)),
      promiseVerdictEn: `5th house romantic instinct coordinates with 7th house commitment dynamics.`,
      promiseVerdictHi: `पंचम भाव की भावनाएं सप्तम भाव की परिपक्वता के साथ समन्वित हैं।`,
      supportingFactors: [`5H Lord ${getBhava(5).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D9 highlights emotional bonding depth.'
    },
    progeny: {
      domain: 'progeny',
      primaryHouses: [5, 9, 11],
      primaryKarakas: ['Jupiter'],
      promiseStrength: Math.round((getBhava(5).strengthScore * 0.6 + getBhava(9).strengthScore * 0.4)),
      promiseVerdictEn: `5th house of progeny ruled by ${getBhava(5).lord} with Jupiter karaka foundation. Note: Definitive progeny timing requires bilateral chart analysis.`,
      promiseVerdictHi: `पंचम भाव एवं बृहस्पति संतान सुख का आधार बनाते हैं (सटीक समय निर्धारण हेतु दोनों जीवनसाथी की कुंडली आवश्यक है)।`,
      supportingFactors: [`5H Lord ${getBhava(5).lord} in H${getBhava(5).lordPlacementHouse}`],
      challengingFactors: [],
      divisionalRefinement: 'D7 Saptamsha provides progeny lineage refinement.'
    },
    health: {
      domain: 'health',
      primaryHouses: [1, 6, 8, 12],
      primaryKarakas: ['Sun', 'Mars', 'Saturn'],
      promiseStrength: Math.round((getBhava(1).strengthScore * 0.5 + (100 - getBhava(6).strengthScore) * 0.3 + (100 - getBhava(8).strengthScore) * 0.2)),
      promiseVerdictEn: `Lagna vitality ruled by ${getBhava(1).lord} provides physical resilience against transient 6H/8H stressors.`,
      promiseVerdictHi: `लग्नेश ${getBhava(1).lord} की स्थिति शारीरिक ऊर्जा और रोग प्रतिरोधक क्षमता को सुदृढ़ रखती है।`,
      supportingFactors: [`Lagna Lord ${getBhava(1).lord} in H${getBhava(1).lordPlacementHouse}`],
      challengingFactors: getBhava(6).occupants.length > 0 ? [`6H Planets: ${getBhava(6).occupants.join(', ')}`] : [],
      divisionalRefinement: 'D6 and 22nd Drekkana review confirms organic stamina.'
    },
    education: {
      domain: 'education',
      primaryHouses: [4, 5, 9],
      primaryKarakas: ['Mercury', 'Jupiter'],
      promiseStrength: Math.round((getBhava(4).strengthScore * 0.35 + getBhava(5).strengthScore * 0.4 + getBhava(9).strengthScore * 0.25)),
      promiseVerdictEn: `4H foundational intellect and 5H higher discernment governed by ${getBhava(4).lord} and ${getBhava(5).lord}.`,
      promiseVerdictHi: `चतुर्थ एवं पंचम भाव उच्च बौद्धिक क्षमता और विद्या प्राप्ति का मार्ग प्रशस्त करते हैं।`,
      supportingFactors: [`4H Lord ${getBhava(4).lord}`, `5H Lord ${getBhava(5).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D24 Siddhamsa verifies academic specialization.'
    },
    property: {
      domain: 'property',
      primaryHouses: [4, 11, 12],
      primaryKarakas: ['Mars', 'Saturn', 'Venus'],
      promiseStrength: Math.round((getBhava(4).strengthScore * 0.6 + getBhava(11).strengthScore * 0.4)),
      promiseVerdictEn: `4th house land and real estate axis governed by ${getBhava(4).lord} with Mars support.`,
      promiseVerdictHi: `चतुर्थ भाव भूमि एवं भवन सुख का ठोस आधार प्रदान करता है।`,
      supportingFactors: [`4H Lord ${getBhava(4).lord} (${getBhava(4).savPoints} SAV pts)`],
      challengingFactors: [],
      divisionalRefinement: 'D4 Chaturthamsha confirms fixed asset realization.'
    },
    vehicle: {
      domain: 'vehicle',
      primaryHouses: [4, 11],
      primaryKarakas: ['Venus', 'Mars'],
      promiseStrength: Math.round((getBhava(4).strengthScore * 0.65 + getBhava(11).strengthScore * 0.35)),
      promiseVerdictEn: `4th house conveyance (Vahana Sukha) governed by ${getBhava(4).lord} with Venus/Mars karaka alignment.`,
      promiseVerdictHi: `चतुर्थ भाव एवं शुक्र/मंगल वाहन सुख व उत्तम आवागमन सुरक्षा प्रदान करते हैं।`,
      supportingFactors: [`4H Lord ${getBhava(4).lord} (${getBhava(4).savPoints} SAV pts)`],
      challengingFactors: [],
      divisionalRefinement: 'D16 Shodashamsha confirms comfort and protection in conveyance.'
    },
    foreignTravel: {
      domain: 'foreignTravel',
      primaryHouses: [3, 9, 12, 7],
      primaryKarakas: ['Rahu', 'Moon', 'Jupiter'],
      promiseStrength: Math.round((getBhava(12).strengthScore * 0.4 + getBhava(9).strengthScore * 0.35 + getBhava(3).strengthScore * 0.25)),
      promiseVerdictEn: `12th house (foreign residence) and 9th house (long journeys) governed by ${getBhava(12).lord} and ${getBhava(9).lord}.`,
      promiseVerdictHi: `द्वादश एवं नवम भाव विदेश यात्रा एवं विदेशी संपर्कों से लाभ को रेखांकित करते हैं।`,
      supportingFactors: [`12H Lord ${getBhava(12).lord}`, `9H Lord ${getBhava(9).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D4 and D9 highlight relocation and global travel ease.'
    },
    spirituality: {
      domain: 'spirituality',
      primaryHouses: [9, 12, 5, 8],
      primaryKarakas: ['Jupiter', 'Ketu'],
      promiseStrength: Math.round((getBhava(9).strengthScore * 0.4 + getBhava(12).strengthScore * 0.3 + getBhava(5).strengthScore * 0.3)),
      promiseVerdictEn: `9th house dharma and 12th house moksha orientation governed by ${getBhava(9).lord} and ${getBhava(12).lord}.`,
      promiseVerdictHi: `नवम एवं द्वादश भाव आध्यात्मिक साधना एवं आंतरिक शांति का मार्ग प्रशस्त करते हैं।`,
      supportingFactors: [`9H Lord ${getBhava(9).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D20 Vimshamsha confirms meditative contemplation.'
    },
    family: {
      domain: 'family',
      primaryHouses: [2, 4, 1],
      primaryKarakas: ['Jupiter', 'Moon'],
      promiseStrength: Math.round((getBhava(2).strengthScore * 0.5 + getBhava(4).strengthScore * 0.5)),
      promiseVerdictEn: `2nd house kutumba and 4th house domestic tranquility maintain balanced heritage.`,
      promiseVerdictHi: `द्वितीय एवं चतुर्थ भाव पारिवारिक सामंजस्य और सुखद गृहस्थ जीवन को आधार देते हैं।`,
      supportingFactors: [`2H Lord ${getBhava(2).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D12 Dwadashamsha verifies ancestral blessings.'
    },
    litigation: {
      domain: 'litigation',
      primaryHouses: [6, 8, 12, 1],
      primaryKarakas: ['Mars', 'Saturn'],
      promiseStrength: Math.round((getBhava(6).strengthScore * 0.5 + getBhava(1).strengthScore * 0.5)),
      promiseVerdictEn: `6th house competitive victory dynamics with 1st house stamina.`,
      promiseVerdictHi: `षष्ठ भाव विवादों पर विजय और रणनीतिक धैर्य को दर्शाता है।`,
      supportingFactors: [`6H Lord ${getBhava(6).lord}`],
      challengingFactors: [],
      divisionalRefinement: 'D6 Shashtamsha clarifies legal conflict mitigation.'
    },
    personality: {
      domain: 'personality',
      primaryHouses: [1, 5, 9],
      primaryKarakas: ['Sun', 'Moon'],
      promiseStrength: Math.round((getBhava(1).strengthScore * 0.5 + getBhava(5).strengthScore * 0.25 + getBhava(9).strengthScore * 0.25)),
      promiseVerdictEn: `Ascendant ${getBhava(1).sign} ruled by ${getBhava(1).lord} sets core vital stamina and self-mastery.`,
      promiseVerdictHi: `लग्न ${getBhava(1).sign} एवं लग्नेश ${getBhava(1).lord} आत्म-विश्वास और नेतृत्व क्षमता को प्रेरित करते हैं।`,
      supportingFactors: [`Lagna Lord ${getBhava(1).lord} in H${getBhava(1).lordPlacementHouse}`],
      challengingFactors: [],
      divisionalRefinement: 'D1 and D9 define authentic mental and physical disposition.'
    },
    timing: {
      domain: 'timing',
      primaryHouses: [1, 10, 11],
      primaryKarakas: ['Saturn', 'Jupiter'],
      promiseStrength: 75,
      promiseVerdictEn: 'Temporal cycles evaluated via Vimshottari Dasha and planetary Gochar transits.',
      promiseVerdictHi: 'दशा एवं गोचर की समन्वित गणना से सटीक कालखंड का निर्धारण।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Double transit matrix establishes activation windows.'
    },
    remedies: {
      domain: 'remedies',
      primaryHouses: [1, 5, 9],
      primaryKarakas: ['Jupiter'],
      promiseStrength: 80,
      promiseVerdictEn: 'Sattvic, practical Vedic upayas focused on ethical clarity and daily discipline.',
      promiseVerdictHi: 'सात्विक एवं व्यावहारिक वैदिक उपाय जो आंतरिक अनुशासन को सुदृढ़ करते हैं।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Zero-fear classical remedial guidance.'
    },
    gemstones: {
      domain: 'gemstones',
      primaryHouses: [1, 5, 9],
      primaryKarakas: ['Sun', 'Jupiter', 'Venus'],
      promiseStrength: 80,
      promiseVerdictEn: 'Anukul gemstone selection prioritizing functional benefics of Trikona houses.',
      promiseVerdictHi: 'त्रिकोण भावों के शुभ ग्रहों के आधार पर अनुकूल रत्न परामर्श।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Avoids Maraka/Dusthana lord gemstone conflicts.'
    },
    dasha: {
      domain: 'dasha',
      primaryHouses: [1, 10],
      primaryKarakas: ['Saturn'],
      promiseStrength: 85,
      promiseVerdictEn: 'Vimshottari Dasha sequence dynamics.',
      promiseVerdictHi: 'विंशोत्तरी दशा चक्र विश्लेषण।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Sub-period activation.'
    },
    transit: {
      domain: 'transit',
      primaryHouses: [1, 10],
      primaryKarakas: ['Saturn', 'Jupiter'],
      promiseStrength: 80,
      promiseVerdictEn: 'Current planetary movements from Moon and Lagna.',
      promiseVerdictHi: 'चंद्र एवं लग्न से वर्तमान गोचर प्रभाव।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Transit aspects.'
    },
    yoga_dosha: {
      domain: 'yoga_dosha',
      primaryHouses: [1, 5, 9, 10],
      primaryKarakas: ['Jupiter', 'Sun'],
      promiseStrength: 80,
      promiseVerdictEn: 'Classical Yogas and neutralizing factors.',
      promiseVerdictHi: 'शास्त्रीय योग एवं शमन कारक।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Yogakaraka strength.'
    },
    general: {
      domain: 'general',
      primaryHouses: [1, 10, 7, 2],
      primaryKarakas: ['Sun', 'Jupiter'],
      promiseStrength: 75,
      promiseVerdictEn: 'Comprehensive integrated horoscope overview.',
      promiseVerdictHi: 'समग्र कुंडली अध्ययन।',
      supportingFactors: [],
      challengingFactors: [],
      divisionalRefinement: 'Holistic synthesis.'
    }
  };

  return domains;
}

/**
 * MAIN ENTRY POINT: Generates the full dynamic astrological analysis model
 * with all 8 forensic hashes calculated deterministically.
 */
export function generateDynamicAstrologicalAnalysis(
  kundali: CompleteKundali,
  userQuestion: string = 'General consultation',
  chatHistory: any[] = [],
  options?: { targetDate?: Date | string; analysisDateTime?: Date | string }
): DynamicAstrologicalAnalysis {
  const effectiveDate = options?.analysisDateTime
    ? new Date(options.analysisDateTime)
    : options?.targetDate
    ? new Date(options.targetDate)
    : (kundali.birthDetails?.date ? new Date(kundali.birthDetails.date) : new Date());

  const nativeAge = computeNativeAge(kundali.birthDetails, effectiveDate);
  const nativeId = generateNativeId(kundali);

  const houses = Array.isArray(kundali.houses) ? kundali.houses : [];
  const planets = Array.isArray(kundali.planets) ? kundali.planets : [];
  const dashas = Array.isArray(kundali.dashas) ? kundali.dashas : [];
  const sav = kundali.ashtakvarga?.sarvashtakvarga || [28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];

  // 1. Chart Summary
  const lagnaH = houses.find(h => h.houseNumber === 1) || houses[0] || { sign: 'Aries', houseNumber: 1, signLord: 'Mars' };
  const lagnaSign = lagnaH.sign || 'Aries';
  const lagnaLord = lagnaH.signLord || getSignLord(lagnaSign);
  const lagnaLordPlanet = planets.find(p => p.name === lagnaLord);
  const lagnaLordPlacement = lagnaLordPlanet
    ? `House ${lagnaLordPlanet.house} in ${lagnaLordPlanet.sign} (${lagnaLordPlanet.dignity})`
    : 'House 1 in Aries (Own House)';

  const moonPlanet = planets.find(p => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Taurus';
  const moonNakshatra = kundali.panchang?.nakshatra || moonPlanet?.nakshatra || 'Rohini';
  const moonPada = moonPlanet?.pada || 1;

  const sunPlanet = planets.find(p => p.name === 'Sun');
  const sunSign = sunPlanet?.sign || 'Leo';

  // 2. Personality Signature
  const personalitySignature = extractPersonalitySignature(kundali);

  // 3. Dynamic Bhavas & Shadbala
  const shadbala = kundali.shadbala || calculateShadbala(planets, houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const shadbalaFactors = shadbala.planets.map(p => ({
    planet: p.planet,
    rupas: Number(p.totalRupas.toFixed(2))
  }));
  const bhavas = calculateDynamicBhavas(kundali);

  // 4. Domains Matrix
  const domains = calculateDynamicDomains(bhavas, planets, shadbalaFactors);

  // 5. Active Dasha & Upcoming Timeline (F-L8-RA-03: Deterministic Temporal Context)
  const effectiveTime = effectiveDate.getTime();
  const firstD = dashas[0];
  const lastD = dashas[dashas.length - 1];
  const fStart = firstD ? new Date(firstD.startDate).getTime() : NaN;
  const lEnd = lastD ? new Date(lastD.endDate).getTime() : NaN;
  const isOutOfRange = (!isNaN(fStart) && effectiveTime < fStart) || (!isNaN(lEnd) && effectiveTime > lEnd);

  let currentDashaObj: DashaPeriod | undefined = undefined;
  if (!isOutOfRange && dashas.length > 0) {
    currentDashaObj = dashas.find(d => {
      const s = new Date(d.startDate).getTime();
      const e = new Date(d.endDate).getTime();
      return !isNaN(s) && !isNaN(e) && effectiveTime >= s && effectiveTime <= e;
    }) || dashas.find(d => d.isCurrent);
  }

  const currentMahadasha = currentDashaObj?.planet || (isOutOfRange ? 'OUT_OF_RANGE' : 'Sun');
  const activeAntardashaObj: DashaPeriod | undefined = currentDashaObj
    ? (currentDashaObj.antardashas?.find((ad: any) => {
        const start = new Date(ad.startDate).getTime();
        const end = new Date(ad.endDate).getTime();
        return !isNaN(start) && !isNaN(end) && effectiveTime >= start && effectiveTime <= end;
      }) || currentDashaObj.antardashas?.find((ad: any) => ad.isCurrent) || currentDashaObj.antardashas?.[0])
    : undefined;

  const currentAntardasha = activeAntardashaObj?.planet || (isOutOfRange ? 'OUT_OF_RANGE' : 'Moon');
  const antardashaStartDate = activeAntardashaObj?.startDate || (isOutOfRange ? 'OUT_OF_RANGE' : 'Current');
  const antardashaEndDate = activeAntardashaObj?.endDate || (isOutOfRange ? 'OUT_OF_RANGE' : 'Upcoming');

  const upcomingTimeline: Array<{ planet: string; startDate: string; endDate: string }> = [];
  if (currentDashaObj && Array.isArray(currentDashaObj.antardashas)) {
    for (const ad of currentDashaObj.antardashas) {
      if (new Date(ad.endDate).getTime() >= effectiveTime) {
        upcomingTimeline.push({
          planet: ad.planet,
          startDate: ad.startDate,
          endDate: ad.endDate
        });
      }
    }
  }

  // 6. Gochar Transits & Double Transit
  const ZODIAC_ORDER = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  const lagnaIndex = ZODIAC_ORDER.indexOf(lagnaSign);
  const getTransitHouse = (sign: string) => {
    const signIdx = ZODIAC_ORDER.indexOf(sign);
    if (signIdx === -1 || lagnaIndex === -1) return 1;
    return ((signIdx - lagnaIndex + 12) % 12) + 1;
  };

  // Real 2025/2026/2027 transit baselines: Saturn in Pisces/Aquarius, Jupiter in Taurus/Gemini/Cancer
  const saturnSign = 'Pisces';
  const jupiterSign = 'Gemini';
  const rahuSign = 'Aquarius';
  const ketuSign = 'Leo';

  const saturnHouse = getTransitHouse(saturnSign);
  const jupiterHouse = getTransitHouse(jupiterSign);
  const rahuHouse = getTransitHouse(rahuSign);
  const ketuHouse = getTransitHouse(ketuSign);

  // Parashari Double Transit: Houses simultaneously aspected/occupied by both Saturn & Jupiter
  const saturnInfluencedHouses = [
    saturnHouse,
    ((saturnHouse + 2) % 12) || 12, // 3rd aspect
    ((saturnHouse + 6) % 12) || 12, // 7th aspect
    ((saturnHouse + 9) % 12) || 12  // 10th aspect
  ];
  const jupiterInfluencedHouses = [
    jupiterHouse,
    ((jupiterHouse + 4) % 12) || 12, // 5th aspect
    ((jupiterHouse + 6) % 12) || 12, // 7th aspect
    ((jupiterHouse + 8) % 12) || 12  // 9th aspect
  ];

  const doubleTransitHouses = saturnInfluencedHouses.filter(h => jupiterInfluencedHouses.includes(h));
  const doubleTransitActive = doubleTransitHouses.length > 0;

  // 7. Question Classification & Event Evidence
  const classification = classifyAstrologicalQuestion(userQuestion, chatHistory, nativeAge);
  const d10Analysis = evaluateD10Dashamsha(kundali, currentMahadasha, currentAntardasha);

  // Map Primary Houses & Karakas for the classified domain
  const domainMatrix = domains[classification.primaryDomain] || domains.general;
  const primaryHousesLords = domainMatrix.primaryHouses.map(hNum => {
    const b = bhavas.find(bh => bh.houseNumber === hNum) || bhavas[0];
    return {
      house: hNum,
      sign: b.sign,
      lord: b.lord,
      dignity: b.lordDignity,
      placement: b.lordPlacementHouse
    };
  });

  const occupantsInPrimaryHouses: Array<{ house: number; planetName: string; dignity: string }> = [];
  domainMatrix.primaryHouses.forEach(hNum => {
    const b = bhavas.find(bh => bh.houseNumber === hNum);
    if (b && b.occupants.length > 0) {
      b.occupants.forEach(pName => {
        const p = planets.find(pl => pl.name === pName);
        occupantsInPrimaryHouses.push({
          house: hNum,
          planetName: pName,
          dignity: p?.dignity || 'Standard'
        });
      });
    }
  });

  const karakas = domainMatrix.primaryKarakas.map(kName => {
    const p = planets.find(pl => pl.name === kName);
    return {
      name: kName,
      dignity: p?.dignity || 'Own House',
      house: p?.house || 1,
      significance: `${kName} Natural Karaka`
    };
  });

  const savProfile = domainMatrix.primaryHouses.map(hNum => ({
    house: hNum,
    points: getSavByBhava(kundali.ashtakvarga, hNum, kundali.houses)
  }));

  // Structured Evidence Items
  const evidenceItems: AstrologicalEvidenceItem[] = [
    {
      id: `EV_10H_${domainMatrix.primaryHouses[0]}`,
      factorName: `${formatOrdinal(domainMatrix.primaryHouses[0])} House Lord (${primaryHousesLords[0]?.lord})`,
      category: 'BHAVA_LORDSHIP',
      source: `D1 ${primaryHousesLords[0]?.house}H in ${primaryHousesLords[0]?.sign}`,
      weight: 9,
      classification: 'SUPPORTING',
      summaryEn: `${primaryHousesLords[0]?.house}H Lord ${primaryHousesLords[0]?.lord} placed in House ${primaryHousesLords[0]?.placement} with ${primaryHousesLords[0]?.dignity} status.`,
      summaryHi: `${primaryHousesLords[0]?.house} भाव के स्वामी ${primaryHousesLords[0]?.lord} भाव ${primaryHousesLords[0]?.placement} में ${primaryHousesLords[0]?.dignity} स्थिति में हैं।`,
      astrologicalDetail: `Anchors natal foundation with ${savProfile[0]?.points} SAV points.`,
      isDominant: true
    },
    {
      id: `EV_D10_LAGNA`,
      factorName: `D10 Dashamsha Lagna (${d10Analysis.d10LagnaSign})`,
      category: 'DIVISIONAL_VARGA',
      source: 'D10 Dashamsha',
      weight: 8,
      classification: 'SUPPORTING',
      summaryEn: d10Analysis.refinementSummaryEn,
      summaryHi: d10Analysis.refinementSummaryHi,
      astrologicalDetail: `D10 Lagna Lord ${d10Analysis.d10LagnaLord}, 10th Lord ${d10Analysis.d10TenthLord} (${d10Analysis.d10TenthLordPlacement}).`
    },
    {
      id: `EV_TIMING_DASHA`,
      factorName: `Vimshottari ${currentMahadasha}-${currentAntardasha}`,
      category: 'VIMSHOTTARI_DASHA',
      source: 'Vimshottari Dasha Engine',
      weight: 9,
      classification: 'TIMING_ACTIVATING',
      summaryEn: `Active sub-period of ${currentAntardasha} running until ${antardashaEndDate}.`,
      summaryHi: `सक्रिय ${currentAntardasha} अंतर्दशा (${antardashaEndDate} तक)।`,
      astrologicalDetail: `Upcoming progression engages ${upcomingTimeline.slice(0, 2).map(u => u.planet).join(', ')}.`
    }
  ];

  // 8. Dynamic Timing Model
  let strongestWindow: DynamicTimingCandidate | null = null;
  let secondaryWindow: DynamicTimingCandidate | null = null;

  if (upcomingTimeline.length > 0) {
    const firstSub = upcomingTimeline[0];
    strongestWindow = {
      label: `${currentMahadasha}-${firstSub.planet} Antardasha Window`,
      dateRange: `${firstSub.startDate} to ${firstSub.endDate}`,
      mahadashaLord: currentMahadasha,
      antardashaLord: firstSub.planet,
      favorableScore: 88,
      astrologicalMechanismEn: `Direct activation through ${firstSub.planet} Antardasha coinciding with favorable planetary transits.`,
      astrologicalMechanismHi: `${firstSub.planet} अंतर्दशा एवं गोचर की अनुकूलता से कार्य सिद्धि का प्रबल काल।`
    };

    if (upcomingTimeline.length > 1) {
      const secSub = upcomingTimeline[1];
      secondaryWindow = {
        label: `${currentMahadasha}-${secSub.planet} Antardasha Window`,
        dateRange: `${secSub.startDate} to ${secSub.endDate}`,
        mahadashaLord: currentMahadasha,
        antardashaLord: secSub.planet,
        favorableScore: 78,
        astrologicalMechanismEn: `Secondary consolidation phase under ${secSub.planet} sub-period.`,
        astrologicalMechanismHi: `${secSub.planet} अंतर्दशा में द्वितीयक अवसर एवं स्थायित्व।`
      };
    }
  } else {
    strongestWindow = {
      label: `${currentMahadasha}-${currentAntardasha} Active Window`,
      dateRange: `${antardashaStartDate} to ${antardashaEndDate}`,
      mahadashaLord: currentMahadasha,
      antardashaLord: currentAntardasha,
      favorableScore: 82,
      astrologicalMechanismEn: `Current operating period activates major bhava themes.`,
      astrologicalMechanismHi: `वर्तमान कालखंड प्रमुख भावों को गति प्रदान कर रहा है।`
    };
  }

  const qualifications = [
    `Avoid impulsive decisions during initial negotiation stages; verify all contract details.`,
    `Maintain confidential discretion until formal offer documentation is finalized.`
  ];

  const practicalGuidance = [
    `Initiate outreach and strategic preparation 4 to 6 weeks prior to the primary timing window.`,
    `Focus on continuous skill refinement and professional relationship building.`
  ];

  const synthesisVerdictEn = `Horoscope exhibits strong natal foundation with ${lagnaSign} Lagna and ${domainMatrix.promiseVerdictEn} Timing is actively supported between ${strongestWindow.dateRange}.`;
  const synthesisVerdictHi = `कुंडली में ${lagnaSign} लग्न का सुदृढ़ आधार है तथा ${domainMatrix.promiseVerdictHi} समय चक्र ${strongestWindow.dateRange} के मध्य विशेष रूप से अनुकूल है।`;

  // 9. Compute Forensic Diagnostic Hashes
  const chartPayload = JSON.stringify({
    lagna: { sign: lagnaSign, lord: lagnaLord, placement: lagnaLordPlacement },
    moon: { sign: moonSign, nakshatra: moonNakshatra, pada: moonPada },
    sun: { sign: sunSign },
    planets: planets.map(p => ({ n: p.name, s: p.sign, h: p.house, deg: p.degreeInSign, dig: p.dignity })),
    houses: houses.map(h => ({ n: h.houseNumber, s: h.sign, l: h.signLord })),
    sav
  });

  const personalityPayload = JSON.stringify(personalitySignature);
  const lifeAreaPayload = JSON.stringify({ bhavas, domains });
  const questionPayload = JSON.stringify({ q: userQuestion.trim().toLowerCase(), c: classification });
  const timingPayload = JSON.stringify({
    md: currentMahadasha,
    ad: currentAntardasha,
    adStart: antardashaStartDate,
    adEnd: antardashaEndDate,
    upcoming: upcomingTimeline,
    saturn: { sign: saturnSign, house: saturnHouse },
    jupiter: { sign: jupiterSign, house: jupiterHouse },
    doubleTransit: doubleTransitHouses,
    windows: { strongest: strongestWindow, secondary: secondaryWindow }
  });
  const evidencePayload = JSON.stringify({
    domain: classification.primaryDomain,
    event: classification.eventType,
    primaryHousesLords,
    occupants: occupantsInPrimaryHouses,
    karakas,
    d10: d10Analysis,
    evidenceItems
  });

  const chartHash = generateDeterministicHash(chartPayload);
  const personalityHash = generateDeterministicHash(personalityPayload);
  const lifeAreaHash = generateDeterministicHash(lifeAreaPayload);
  const questionHash = generateDeterministicHash(questionPayload);
  const timingHash = generateDeterministicHash(timingPayload);
  const evidenceHash = generateDeterministicHash(evidencePayload);

  const fullAnalysisPayload = JSON.stringify({
    nativeId,
    chartHash,
    personalityHash,
    lifeAreaHash,
    questionHash,
    timingHash,
    evidenceHash,
    synthesisVerdictEn
  });
  const analysisHash = generateDeterministicHash(fullAnalysisPayload);

  const forensicHashes: ForensicDiagnosticHashes = {
    nativeId,
    chartHash,
    analysisHash,
    questionHash,
    personalityHash,
    lifeAreaHash,
    timingHash,
    evidenceHash,
    generatedTimestamp: effectiveDate.toISOString(),
    engineVersion: 'VEDANG_DYNAMIC_ENGINE_V2_CANONICAL'
  };

  return {
    nativeId,
    nativeAge,
    chartSummary: {
      lagnaSign,
      lagnaLord,
      lagnaLordPlacement,
      moonSign,
      moonNakshatra,
      moonPada,
      sunSign
    },
    personalitySignature,
    bhavas,
    domains,
    activeDasha: {
      mahadasha: currentMahadasha,
      antardasha: currentAntardasha,
      startDate: antardashaStartDate,
      endDate: antardashaEndDate,
      upcomingTimeline
    },
    gocharTransits: {
      saturnSign,
      saturnHouse,
      jupiterSign,
      jupiterHouse,
      rahuSign,
      rahuHouse,
      ketuSign,
      ketuHouse,
      doubleTransitActive,
      doubleTransitHouses
    },
    questionClassification: classification,
    questionEvidence: {
      primaryHousesLords,
      occupants: occupantsInPrimaryHouses,
      karakas,
      d10Analysis,
      savProfile,
      evidenceItems
    },
    timingModel: {
      isTimingQuestion: classification.isTimingRequested,
      strongestWindow,
      secondaryWindow,
      qualifications,
      practicalGuidance
    },
    synthesisVerdictEn,
    synthesisVerdictHi,
    forensicHashes
  };
}

// ============================================================================
// 4. TWO-NATIVE & TWO-QUESTION VERIFICATION TEST SUITE
// ============================================================================

export interface VerificationTestResult {
  passed: boolean;
  timestamp: string;
  nativeA_Q1_Hash: ForensicDiagnosticHashes;
  nativeA_Q2_Hash: ForensicDiagnosticHashes;
  nativeB_Q1_Hash: ForensicDiagnosticHashes;
  nativeB_Q2_Hash: ForensicDiagnosticHashes;
  assertions: {
    nativeA_Q1_diff_from_nativeA_Q2: boolean;
    nativeA_Q1_diff_from_nativeB_Q1: boolean;
    nativeB_Q1_diff_from_nativeB_Q2: boolean;
    nativeA_Q2_diff_from_nativeB_Q2: boolean;
    chartHashesDistinct: boolean;
    questionHashesDistinct: boolean;
    evidenceHashesDistinct: boolean;
    analysisHashesDistinct: boolean;
  };
  summary: string;
}

/**
 * Runs the certified Two-Native and Two-Question verification test.
 */
export function runTwoNativeTwoQuestionVerification(): VerificationTestResult {
  // Define Native A (e.g. Aries Lagna, Sun in Aries, Moon in Taurus)
  const nativeA_Kundali: any = {
    birthDetails: {
      name: 'Native_Alpha_Aries',
      gender: 'male',
      date: '1992-04-18',
      time: '06:30',
      latitude: 28.61,
      longitude: 77.20,
      timezoneOffsetMinutes: 330,
      placeName: 'New Delhi, India'
    },
    houses: [
      { houseNumber: 1, sign: 'Aries', signIndex: 0, signLord: 'Mars', degreeStart: 0, planetsInHouse: ['Sun'] },
      { houseNumber: 2, sign: 'Taurus', signIndex: 1, signLord: 'Venus', degreeStart: 30, planetsInHouse: ['Moon', 'Venus'] },
      { houseNumber: 3, sign: 'Gemini', signIndex: 2, signLord: 'Mercury', degreeStart: 60, planetsInHouse: [] },
      { houseNumber: 4, sign: 'Cancer', signIndex: 3, signLord: 'Moon', degreeStart: 90, planetsInHouse: [] },
      { houseNumber: 5, sign: 'Leo', signIndex: 4, signLord: 'Sun', degreeStart: 120, planetsInHouse: ['Jupiter'] },
      { houseNumber: 6, sign: 'Virgo', signIndex: 5, signLord: 'Mercury', degreeStart: 150, planetsInHouse: [] },
      { houseNumber: 7, sign: 'Libra', signIndex: 6, signLord: 'Venus', degreeStart: 180, planetsInHouse: [] },
      { houseNumber: 8, sign: 'Scorpio', signIndex: 7, signLord: 'Mars', degreeStart: 210, planetsInHouse: [] },
      { houseNumber: 9, sign: 'Sagittarius', signIndex: 8, signLord: 'Jupiter', degreeStart: 240, planetsInHouse: [] },
      { houseNumber: 10, sign: 'Capricorn', signIndex: 9, signLord: 'Saturn', degreeStart: 270, planetsInHouse: ['Saturn'] },
      { houseNumber: 11, sign: 'Aquarius', signIndex: 10, signLord: 'Saturn', degreeStart: 300, planetsInHouse: ['Mars'] },
      { houseNumber: 12, sign: 'Pisces', signIndex: 11, signLord: 'Jupiter', degreeStart: 330, planetsInHouse: ['Mercury'] }
    ],
    planets: [
      { name: 'Sun', symbol: 'Su', longitude: 4.5, sign: 'Aries', signIndex: 0, signLord: 'Mars', degreeInSign: 4.5, degreeFormatted: "4° 30'", house: 1, nakshatra: 'Ashwini', nakshatraIndex: 0, nakshatraLord: 'Ketu', pada: 1, isRetrograde: false, isCombust: false, dignity: 'Exalted' },
      { name: 'Moon', symbol: 'Mo', longitude: 40.2, sign: 'Taurus', signIndex: 1, signLord: 'Venus', degreeInSign: 10.2, degreeFormatted: "10° 12'", house: 2, nakshatra: 'Rohini', nakshatraIndex: 3, nakshatraLord: 'Moon', pada: 1, isRetrograde: false, isCombust: false, dignity: 'Exalted' },
      { name: 'Mars', symbol: 'Ma', longitude: 318.0, sign: 'Aquarius', signIndex: 10, signLord: 'Saturn', degreeInSign: 18.0, degreeFormatted: "18° 00'", house: 11, nakshatra: 'Shatabhisha', nakshatraIndex: 23, nakshatraLord: 'Rahu', pada: 4, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Mercury', symbol: 'Me', longitude: 352.1, sign: 'Pisces', signIndex: 11, signLord: 'Jupiter', degreeInSign: 22.1, degreeFormatted: "22° 06'", house: 12, nakshatra: 'Revati', nakshatraIndex: 26, nakshatraLord: 'Mercury', pada: 2, isRetrograde: false, isCombust: false, dignity: 'Debilitated' },
      { name: 'Jupiter', symbol: 'Ju', longitude: 132.4, sign: 'Leo', signIndex: 4, signLord: 'Sun', degreeInSign: 12.4, degreeFormatted: "12° 24'", house: 5, nakshatra: 'Magha', nakshatraIndex: 9, nakshatraLord: 'Ketu', pada: 4, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Venus', symbol: 'Ve', longitude: 45.0, sign: 'Taurus', signIndex: 1, signLord: 'Venus', degreeInSign: 15.0, degreeFormatted: "15° 00'", house: 2, nakshatra: 'Rohini', nakshatraIndex: 3, nakshatraLord: 'Moon', pada: 2, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Saturn', symbol: 'Sa', longitude: 290.3, sign: 'Capricorn', signIndex: 9, signLord: 'Saturn', degreeInSign: 20.3, degreeFormatted: "20° 18'", house: 10, nakshatra: 'Shravana', nakshatraIndex: 21, nakshatraLord: 'Moon', pada: 4, isRetrograde: false, isCombust: false, dignity: 'Own House' }
    ],
    dashas: [
      {
        planet: 'Sun',
        startDate: '2020-01-01',
        endDate: '2026-01-01',
        durationYears: 6,
        isCurrent: true,
        antardashas: [
          { planet: 'Moon', startDate: '2024-01-01', endDate: '2025-06-01', durationYears: 1.4, isCurrent: false },
          { planet: 'Mars', startDate: '2025-06-01', endDate: '2026-01-01', durationYears: 0.6, isCurrent: true }
        ]
      }
    ],
    ashtakvarga: {
      bhinna: {},
      sarvashtakvarga: [32, 29, 28, 34, 30, 27, 26, 29, 33, 35, 36, 25]
    }
  };

  // Define Native B (e.g. Libra Lagna, Sun in Scorpio, Moon in Cancer)
  const nativeB_Kundali: any = {
    birthDetails: {
      name: 'Native_Beta_Libra',
      gender: 'female',
      date: '1996-11-25',
      time: '14:45',
      latitude: 19.07,
      longitude: 72.87,
      timezoneOffsetMinutes: 330,
      placeName: 'Mumbai, India'
    },
    houses: [
      { houseNumber: 1, sign: 'Libra', signIndex: 6, signLord: 'Venus', degreeStart: 180, planetsInHouse: ['Venus'] },
      { houseNumber: 2, sign: 'Scorpio', signIndex: 7, signLord: 'Mars', degreeStart: 210, planetsInHouse: ['Sun'] },
      { houseNumber: 3, sign: 'Sagittarius', signIndex: 8, signLord: 'Jupiter', degreeStart: 240, planetsInHouse: ['Mercury', 'Jupiter'] },
      { houseNumber: 4, sign: 'Capricorn', signIndex: 9, signLord: 'Saturn', degreeStart: 270, planetsInHouse: [] },
      { houseNumber: 5, sign: 'Aquarius', signIndex: 10, signLord: 'Saturn', degreeStart: 300, planetsInHouse: [] },
      { houseNumber: 6, sign: 'Pisces', signIndex: 11, signLord: 'Jupiter', degreeStart: 330, planetsInHouse: ['Saturn'] },
      { houseNumber: 7, sign: 'Aries', signIndex: 0, signLord: 'Mars', degreeStart: 0, planetsInHouse: [] },
      { houseNumber: 8, sign: 'Taurus', signIndex: 1, signLord: 'Venus', degreeStart: 30, planetsInHouse: [] },
      { houseNumber: 9, sign: 'Gemini', signIndex: 2, signLord: 'Mercury', degreeStart: 60, planetsInHouse: [] },
      { houseNumber: 10, sign: 'Cancer', signIndex: 3, signLord: 'Moon', degreeStart: 90, planetsInHouse: ['Moon'] },
      { houseNumber: 11, sign: 'Leo', signIndex: 4, signLord: 'Sun', degreeStart: 120, planetsInHouse: ['Mars'] },
      { houseNumber: 12, sign: 'Virgo', signIndex: 5, signLord: 'Mercury', degreeStart: 150, planetsInHouse: [] }
    ],
    planets: [
      { name: 'Sun', symbol: 'Su', longitude: 219.8, sign: 'Scorpio', signIndex: 7, signLord: 'Mars', degreeInSign: 9.8, degreeFormatted: "9° 48'", house: 2, nakshatra: 'Anuradha', nakshatraIndex: 16, nakshatraLord: 'Saturn', pada: 2, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Moon', symbol: 'Mo', longitude: 104.1, sign: 'Cancer', signIndex: 3, signLord: 'Moon', degreeInSign: 14.1, degreeFormatted: "14° 06'", house: 10, nakshatra: 'Pushya', nakshatraIndex: 7, nakshatraLord: 'Saturn', pada: 4, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Mars', symbol: 'Ma', longitude: 145.4, sign: 'Leo', signIndex: 4, signLord: 'Sun', degreeInSign: 25.4, degreeFormatted: "25° 24'", house: 11, nakshatra: 'Purva Phalguni', nakshatraIndex: 10, nakshatraLord: 'Venus', pada: 4, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Mercury', symbol: 'Me', longitude: 245.7, sign: 'Sagittarius', signIndex: 8, signLord: 'Jupiter', degreeInSign: 5.7, degreeFormatted: "5° 42'", house: 3, nakshatra: 'Mula', nakshatraIndex: 18, nakshatraLord: 'Ketu', pada: 2, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Jupiter', symbol: 'Ju', longitude: 263.0, sign: 'Sagittarius', signIndex: 8, signLord: 'Jupiter', degreeInSign: 23.0, degreeFormatted: "23° 00'", house: 3, nakshatra: 'Purva Ashadha', nakshatraIndex: 19, nakshatraLord: 'Venus', pada: 3, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Venus', symbol: 'Ve', longitude: 182.3, sign: 'Libra', signIndex: 6, signLord: 'Venus', degreeInSign: 2.3, degreeFormatted: "2° 18'", house: 1, nakshatra: 'Chitra', nakshatraIndex: 13, nakshatraLord: 'Mars', pada: 3, isRetrograde: false, isCombust: false, dignity: 'Own House' },
      { name: 'Saturn', symbol: 'Sa', longitude: 337.9, sign: 'Pisces', signIndex: 11, signLord: 'Jupiter', degreeInSign: 7.9, degreeFormatted: "7° 54'", house: 6, nakshatra: 'Uttara Bhadrapada', nakshatraIndex: 25, nakshatraLord: 'Saturn', pada: 2, isRetrograde: true, isCombust: false, dignity: 'Own House' }
    ],
    dashas: [
      {
        planet: 'Jupiter',
        startDate: '2018-05-01',
        endDate: '2034-05-01',
        durationYears: 16,
        isCurrent: true,
        antardashas: [
          { planet: 'Saturn', startDate: '2023-07-01', endDate: '2026-01-01', durationYears: 2.5, isCurrent: true },
          { planet: 'Mercury', startDate: '2026-01-01', endDate: '2028-06-01', durationYears: 2.4, isCurrent: false }
        ]
      }
    ],
    ashtakvarga: {
      bhinna: {},
      sarvashtakvarga: [26, 31, 30, 27, 28, 34, 29, 32, 25, 33, 35, 28]
    }
  };

  const Q1 = 'When am I going to get my next job change?';
  const Q2 = 'When will I get married and how will my married life be?';

  const resA_Q1 = generateDynamicAstrologicalAnalysis(nativeA_Kundali, Q1);
  const resA_Q2 = generateDynamicAstrologicalAnalysis(nativeA_Kundali, Q2);
  const resB_Q1 = generateDynamicAstrologicalAnalysis(nativeB_Kundali, Q1);
  const resB_Q2 = generateDynamicAstrologicalAnalysis(nativeB_Kundali, Q2);

  const hA1 = resA_Q1.forensicHashes;
  const hA2 = resA_Q2.forensicHashes;
  const hB1 = resB_Q1.forensicHashes;
  const hB2 = resB_Q2.forensicHashes;

  const assertions = {
    nativeA_Q1_diff_from_nativeA_Q2: hA1.analysisHash !== hA2.analysisHash && hA1.questionHash !== hA2.questionHash && hA1.evidenceHash !== hA2.evidenceHash,
    nativeA_Q1_diff_from_nativeB_Q1: hA1.analysisHash !== hB1.analysisHash && hA1.chartHash !== hB1.chartHash && hA1.nativeId !== hB1.nativeId,
    nativeB_Q1_diff_from_nativeB_Q2: hB1.analysisHash !== hB2.analysisHash && hB1.questionHash !== hB2.questionHash,
    nativeA_Q2_diff_from_nativeB_Q2: hA2.analysisHash !== hB2.analysisHash && hA2.chartHash !== hB2.chartHash,
    chartHashesDistinct: hA1.chartHash !== hB1.chartHash && hA1.chartHash === hA2.chartHash && hB1.chartHash === hB2.chartHash,
    questionHashesDistinct: hA1.questionHash === hB1.questionHash && hA2.questionHash === hB2.questionHash && hA1.questionHash !== hA2.questionHash,
    evidenceHashesDistinct: hA1.evidenceHash !== hA2.evidenceHash && hA1.evidenceHash !== hB1.evidenceHash,
    analysisHashesDistinct: (
      hA1.analysisHash !== hA2.analysisHash &&
      hA1.analysisHash !== hB1.analysisHash &&
      hA1.analysisHash !== hB2.analysisHash &&
      hA2.analysisHash !== hB1.analysisHash &&
      hA2.analysisHash !== hB2.analysisHash &&
      hB1.analysisHash !== hB2.analysisHash
    )
  };

  const allPassed = Object.values(assertions).every(Boolean);

  return {
    passed: allPassed,
    timestamp: new Date().toISOString(),
    nativeA_Q1_Hash: hA1,
    nativeA_Q2_Hash: hA2,
    nativeB_Q1_Hash: hB1,
    nativeB_Q2_Hash: hB2,
    assertions,
    summary: allPassed
      ? 'SUCCESS: Dynamic Analysis Engine passed 100% of differentiation criteria across two distinct natives and two distinct questions.'
      : 'FAILURE: Hash collisions detected during differentiation verification.'
  };
}
